import { FEMachine, LoadPoint, ScenarioDelta } from '@/types/flexe';

/**
 * Convert ampère to kW (3-phase or single phase)
 */
export function toKW(
  amps: number, 
  cosphi = 0.9, 
  phases = 3, 
  voltage = 400
): number {
  if (phases === 3) {
    return Math.sqrt(3) * voltage * amps * cosphi / 1000;
  } else {
    return voltage * amps * cosphi / 1000;
  }
}

/**
 * Check if machine is active at given time based on schedule
 */
export function isActive(
  datetime: Date, 
  schedule?: Record<string, string[][]>
): boolean {
  if (!schedule || Object.keys(schedule).length === 0) return true;
  
  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayName = dayNames[datetime.getDay()];
  const slots = schedule[dayName] || [];
  
  if (slots.length === 0) return false;
  
  const hours = datetime.getHours();
  const minutes = datetime.getMinutes();
  const currentMinutes = hours * 60 + minutes;
  
  return slots.some(([start, end]) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  });
}

/**
 * Check if time is within apply window
 */
export function inApplyWindow(
  datetime: Date, 
  window?: { start: string; end: string }
): boolean {
  if (!window) return true;
  
  const hours = datetime.getHours();
  const minutes = datetime.getMinutes();
  const currentMinutes = hours * 60 + minutes;
  
  const [startH, startM] = window.start.split(':').map(Number);
  const [endH, endM] = window.end.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  
  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}

/**
 * Calculate machine power at specific time
 */
export function machineKWAt(datetime: Date, machine: FEMachine): number {
  const baseKW = machine.kw ?? toKW(
    machine.amps || 0, 
    machine.cosphi, 
    machine.phases, 
    machine.voltage
  );
  
  const duty = machine.duty_cycle;
  const conc = machine.concurrency_factor;
  const units = machine.units;
  const active = isActive(datetime, machine.schedule) ? 1 : 0;
  
  return baseKW * duty * conc * units * active;
}

/**
 * Apply scenario to baseline load profile
 */
export function applyScenarioDelta(
  baselineLoad: LoadPoint[],
  newMachines: FEMachine[],
  replacedMachines: FEMachine[],
  applyWindow?: { start: string; end: string }
): ScenarioDelta[] {
  return baselineLoad.map(point => {
    const datetime = new Date(point.timestamp);
    
    // Check if we should apply changes at this time
    if (!inApplyWindow(datetime, applyWindow)) {
      return {
        timestamp: point.timestamp,
        baseline_kw: point.kw,
        scenario_kw: point.kw,
        delta_kw: 0
      };
    }
    
    // Calculate additions
    const additions = newMachines.reduce((sum, m) => 
      sum + machineKWAt(datetime, m), 0
    );
    
    // Calculate removals
    const removals = replacedMachines.reduce((sum, m) => 
      sum + machineKWAt(datetime, m), 0
    );
    
    const delta = additions - removals;
    const scenarioKW = Math.max(0, point.kw + delta);
    
    return {
      timestamp: point.timestamp,
      baseline_kw: point.kw,
      scenario_kw: scenarioKW,
      delta_kw: delta
    };
  });
}
